import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

/* ✅ Dynamic API base URL — works locally & on HTTPS domain */
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";
const getAuthConfig = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("userToken");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    timeout: 30000,
  };
};

const BEFORE_COLOR = "#5F259F";
const AFTER_COLOR = "#28a745";

// First name of the logged-in user, for the greeting
const getFirstName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const name = user?.name || user?.fullName || user?.username || "";
    return String(name).trim().split(" ")[0];
  } catch {
    return "";
  }
};

const Homepage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState("");
  const navigate = useNavigate();

  /* ---------- Fetch projects ---------- */
  useEffect(() => {
    // 1️⃣ Instantly show cached data (for faster load)
    const cached = localStorage.getItem("projects");
    if (cached) {
      setProjects(JSON.parse(cached));
      setLoading(false);
    }

    // 2️⃣ Fetch fresh data in background
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      // Sends the login token (the request had no token before)
      const res = await axios.get(`${BASE_URL}/api/projects`, getAuthConfig());
      const data = res.data || [];
      setProjects(data);
      localStorage.setItem("projects", JSON.stringify(data)); // cache it
    } catch (err) {
      console.error("❌ Error loading projects:", err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Categorization ---------- */
  const beforeBooking = projects.filter((p) => {
    const totalPaid = Number(p.totalPaid || 0);
    const finalAmount = Number(p.finalAmount || 0);
    const hasPayment = !!p.hasPayment || totalPaid > 0;

    return (!hasPayment || finalAmount === 0) && !p.isArchived;
  });

  const afterBooking = projects.filter((p) => {
    const totalPaid = Number(p.totalPaid || 0);
    const finalAmount = Number(p.finalAmount || 0);
    const hasPayment = !!p.hasPayment || totalPaid > 0;
    const isClosed = p.isClosed === true;

    return hasPayment && finalAmount > 0 && !isClosed;
  });

  const beforeCount = beforeBooking.length;
  const afterCount = afterBooking.length;
  const activeTotal = beforeCount + afterCount;
  const beforePercent = activeTotal
    ? Math.round((beforeCount / activeTotal) * 100)
    : 0;
  const afterPercent = activeTotal ? 100 - beforePercent : 0;

  const firstName = getFirstName();

  // Cards work with the keyboard too (Enter / Space)
  const onCardKey = (event, path) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigate(path);
    }
  };

  /* ---------- One stage card ---------- */
  const renderCard = ({
    id,
    path,
    color,
    emoji,
    stage,
    title,
    text,
    count,
  }) => {
    const lifted = hoveredCard === id;

    return (
      <div
        key={id}
        role="button"
        tabIndex={0}
        onClick={() => navigate(path)}
        onKeyDown={(e) => onCardKey(e, path)}
        onMouseEnter={() => setHoveredCard(id)}
        onMouseLeave={() => setHoveredCard("")}
        onFocus={() => setHoveredCard(id)}
        onBlur={() => setHoveredCard("")}
        style={{
          ...styles.card,
          transform: lifted ? "translateY(-8px)" : "translateY(0)",
          boxShadow: lifted
            ? `0 22px 44px ${color}33`
            : "0 8px 24px rgba(15, 23, 42, 0.08)",
          borderColor: lifted ? `${color}66` : "#e8edf3",
        }}
      >
        {/* Coloured top edge */}
        <div
          aria-hidden="true"
          style={{
            ...styles.cardTopBar,
            backgroundImage: `linear-gradient(90deg, ${color}, ${color}99)`,
          }}
        />

        {/* Icon + stage label */}
        <div style={styles.cardHeadRow}>
          <div
            style={{
              ...styles.iconBadge,
              backgroundImage: `linear-gradient(135deg, ${color}, ${color}bb)`,
              boxShadow: `0 10px 20px ${color}44`,
            }}
          >
            {emoji}
          </div>

          <span
            style={{
              ...styles.stagePill,
              color,
              backgroundColor: `${color}14`,
              borderColor: `${color}30`,
            }}
          >
            {stage}
          </span>
        </div>

        <h3 style={{ ...styles.cardTitle, color }}>{title}</h3>

        {/* Big number */}
        <div style={styles.countRow}>
          <span style={{ ...styles.countNumber, color }}>{count}</span>
          <span style={styles.countLabel}>
            {count === 1 ? "project" : "projects"}
          </span>
        </div>

        <p style={styles.cardText}>{text}</p>

        {/* Call to action */}
        <div
          style={{
            ...styles.cardButton,
            backgroundColor: lifted ? color : `${color}12`,
            color: lifted ? "#ffffff" : color,
          }}
        >
          View projects
          <span
            aria-hidden="true"
            style={{
              ...styles.cardArrow,
              transform: lifted ? "translateX(4px)" : "translateX(0)",
            }}
          >
            →
          </span>
        </div>
      </div>
    );
  };

  /* ---------- UI ---------- */
  return (
    <div style={styles.container}>
      {/* Heading */}
      <div style={styles.header}>
        <div style={styles.eyebrow}>PROJECT OVERVIEW</div>

        <h2 style={styles.heading}>
          {firstName ? `Welcome back, ${firstName}` : "Project Overview"}
        </h2>

        <p style={styles.subHeading}>Choose a stage to see its projects.</p>
      </div>

      {loading && projects.length === 0 ? (
        <p style={styles.loading}>⏳ Loading projects...</p>
      ) : (
        <>
          <div style={styles.cardContainer}>
            {renderCard({
              id: "before",
              path: "/before-booking",
              color: BEFORE_COLOR,
              emoji: "🕒",
              stage: "STAGE 1",
              title: "Before Booking",
              text: "Projects waiting for a booking payment and an approved amount.",
              count: beforeCount,
            })}

            {renderCard({
              id: "after",
              path: "/after-booking",
              color: AFTER_COLOR,
              emoji: "💰",
              stage: "STAGE 2",
              title: "After Booking",
              text: "Booked projects with payment received and work in progress.",
              count: afterCount,
            })}
          </div>

          {/* Pipeline split bar */}
          {activeTotal > 0 && (
            <div style={styles.splitCard}>
              <div style={styles.splitTop}>
                <span style={styles.splitTitle}>Pipeline split</span>
                <span style={styles.splitTotal}>
                  {activeTotal} active{" "}
                  {activeTotal === 1 ? "project" : "projects"}
                </span>
              </div>

              <div
                style={styles.splitTrack}
                role="img"
                aria-label={`${beforePercent}% before booking, ${afterPercent}% after booking`}
              >
                <div
                  style={{
                    ...styles.splitFill,
                    width: `${beforePercent}%`,
                    backgroundColor: BEFORE_COLOR,
                  }}
                />
                <div
                  style={{
                    ...styles.splitFill,
                    width: `${afterPercent}%`,
                    backgroundColor: AFTER_COLOR,
                  }}
                />
              </div>

              <div style={styles.splitLegend}>
                <span style={styles.legendItem}>
                  <span
                    style={{
                      ...styles.legendDot,
                      backgroundColor: BEFORE_COLOR,
                    }}
                  />
                  Before Booking · {beforePercent}%
                </span>

                <span style={styles.legendItem}>
                  <span
                    style={{
                      ...styles.legendDot,
                      backgroundColor: AFTER_COLOR,
                    }}
                  />
                  After Booking · {afterPercent}%
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ---------- Styles (inline) ----------
   clamp(min, fluid, max) makes sizes shrink smoothly on a phone and stay
   the same on a laptop. */
const styles = {
  container: {
    padding: "clamp(20px, 5vw, 44px) clamp(12px, 3vw, 24px)",
    backgroundColor: "#f4f7fb",
    backgroundImage:
      "radial-gradient(circle at 8% 0%, rgba(95,37,159,0.10), transparent 38%), radial-gradient(circle at 92% 8%, rgba(40,167,69,0.10), transparent 38%), linear-gradient(180deg, #f9fbfd 0%, #eef2f7 100%)",
    minHeight: "calc(100vh - 150px)",
    boxSizing: "border-box",
    borderRadius: "16px",
  },
  header: {
    textAlign: "center",
    marginBottom: "clamp(24px, 5vw, 42px)",
  },
  eyebrow: {
    display: "inline-block",
    marginBottom: "10px",
    padding: "5px 14px",
    color: "#23868a",
    backgroundColor: "rgba(35,134,138,0.1)",
    border: "1px solid rgba(35,134,138,0.2)",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "2px",
  },
  heading: {
    margin: 0,
    fontSize: "clamp(24px, 6vw, 36px)",
    fontWeight: "800",
    color: "#1f2340",
    letterSpacing: "-0.6px",
    overflowWrap: "anywhere",
  },
  subHeading: {
    margin: "10px 0 0",
    fontSize: "clamp(14px, 3.6vw, 16px)",
    color: "#64748b",
  },
  cardContainer: {
    display: "flex",
    gap: "clamp(16px, 4vw, 36px)",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  card: {
    position: "relative",
    overflow: "hidden",
    width: "100%",
    maxWidth: "380px",
    padding: "clamp(24px, 5vw, 32px)",
    paddingTop: "clamp(28px, 5vw, 36px)",
    backgroundColor: "#ffffff",
    border: "1px solid #e8edf3",
    borderRadius: "22px",
    textAlign: "left",
    cursor: "pointer",
    boxSizing: "border-box",
    outline: "none",
    transition:
      "transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease",
  },
  cardTopBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
  },
  cardHeadRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "18px",
  },
  iconBadge: {
    display: "grid",
    placeItems: "center",
    width: "58px",
    height: "58px",
    borderRadius: "18px",
    fontSize: "27px",
  },
  stagePill: {
    padding: "6px 12px",
    border: "1px solid",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "1.4px",
  },
  cardTitle: {
    margin: "0 0 6px",
    fontSize: "clamp(20px, 5vw, 24px)",
    fontWeight: "800",
    letterSpacing: "-0.3px",
  },
  countRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "10px",
    marginBottom: "10px",
  },
  countNumber: {
    fontSize: "clamp(44px, 12vw, 58px)",
    fontWeight: "800",
    lineHeight: 1,
    letterSpacing: "-2px",
  },
  countLabel: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#64748b",
  },
  cardText: {
    margin: "0 0 22px",
    fontSize: "14px",
    lineHeight: 1.6,
    color: "#64748b",
  },
  cardButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    minHeight: "48px",
    borderRadius: "14px",
    fontSize: "15px",
    fontWeight: "800",
    transition: "background-color 0.25s ease, color 0.25s ease",
  },
  cardArrow: {
    display: "inline-block",
    fontSize: "18px",
    transition: "transform 0.25s ease",
  },
  splitCard: {
    maxWidth: "796px",
    margin: "clamp(22px, 5vw, 36px) auto 0",
    padding: "clamp(16px, 4vw, 22px)",
    backgroundColor: "#ffffff",
    border: "1px solid #e8edf3",
    borderRadius: "18px",
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.06)",
    boxSizing: "border-box",
  },
  splitTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "6px 12px",
    marginBottom: "14px",
  },
  splitTitle: {
    fontSize: "15px",
    fontWeight: "800",
    color: "#1f2340",
  },
  splitTotal: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748b",
  },
  splitTrack: {
    display: "flex",
    width: "100%",
    height: "14px",
    backgroundColor: "#e8edf3",
    borderRadius: "999px",
    overflow: "hidden",
  },
  splitFill: {
    height: "100%",
    transition: "width 0.5s ease",
  },
  splitLegend: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 24px",
    marginTop: "14px",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
  },
  legendDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
  },
  loading: {
    textAlign: "center",
    color: "#555",
    fontSize: "20px",
    fontWeight: "500",
    marginTop: "60px",
  },
};

export default Homepage;
